import { useQuery } from '@tanstack/react-query';
import { AlertCircle, AlertTriangle, GitBranch, Inbox, Route, Users } from 'lucide-react';
import { Button, Spinner } from '@centraoneplus/ui';
import { teamsService } from '../../../services/teams.service';

interface MoveTeamConfirmationProps {
  teamId: string;
  teamName: string;
  /** The parent the team moves under; null moves it back to the top level. */
  target: { id: string; name: string } | null;
  /** Called with whether the new parent's own agents move into the team. */
  onConfirm: (moveParentAgents: boolean) => void;
  onCancel: () => void;
  isSaving: boolean;
}

/**
 * The confirmation shown before a team moves under a parent (or back to the
 * top level). Everything it says comes from the server's preview, so the
 * rules live in one place: what switches, what moves with it, what is left
 * behind, and why the move is not allowed when it is not.
 */
export function MoveTeamConfirmation({
  teamId,
  teamName,
  target,
  onConfirm,
  onCancel,
  isSaving,
}: MoveTeamConfirmationProps) {
  const { data: preview, isLoading, error } = useQuery({
    queryKey: ['teams', 'attach-preview', teamId, target?.id ?? null],
    queryFn: () => teamsService.getAttachPreview(teamId, target?.id ?? null),
    staleTime: 0,
  });

  const headline = target
    ? `Make ${teamName} a sub-team of ${target.name}?`
    : `Move ${teamName} back to the top level?`;

  return (
    <div className="space-y-4" data-testid="move-team-confirmation">
      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{headline}</p>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Spinner size="sm" /> Checking what this changes…
        </div>
      ) : error || !preview ? (
        <div className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          Couldn't check this move. Please try again.
        </div>
      ) : (
        <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
          {preview.problems.map((problem) => (
            <li
              key={problem}
              className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-800 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              {problem}
            </li>
          ))}

          {preview.pipelineChange && (
            <li className="flex items-start gap-2">
              <GitBranch className="mt-0.5 h-4 w-4 flex-shrink-0 text-purple-500" />
              <span>
                {teamName} works <strong>{preview.pipelineChange.fromPipelineName ?? 'another pipeline'}</strong>
                ; as a sub-team of {target?.name} it switches to{' '}
                <strong>{preview.pipelineChange.toPipelineName ?? "the parent's pipeline"}</strong>.
              </span>
            </li>
          )}

          {preview.routingReferences.length > 0 && (
            <li className="flex items-start gap-2">
              <Route className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-500" />
              <span>
                Routing rules in {preview.routingReferences.join(', ')} send tickets to {teamName}. They
                keep working; review them afterwards if they should point somewhere else.
              </span>
            </li>
          )}

          {preview.parentAgentsToMove && target && (
            <li className="flex items-start gap-2">
              <Users className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
              <span>
                The {preview.parentAgentsToMove.agentCount}{' '}
                {preview.parentAgentsToMove.agentCount === 1 ? 'agent' : 'agents'} in {target.name} move
                into {teamName}, because a team with sub-teams holds no agents of its own
                {preview.parentAgentsToMove.ticketCount > 0 && (
                  <>
                    , and their {preview.parentAgentsToMove.ticketCount} open {target.name}{' '}
                    {preview.parentAgentsToMove.ticketCount === 1 ? 'ticket moves' : 'tickets move'} with them
                  </>
                )}
                .
              </span>
            </li>
          )}

          {preview.oldParentLeftEmpty && (
            <li className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>
                {preview.oldParentLeftEmpty.name} will have no sub-teams and no agents left
                {preview.oldParentLeftEmpty.waitingTickets > 0 && (
                  <>
                    ; its {preview.oldParentLeftEmpty.waitingTickets} waiting{' '}
                    {preview.oldParentLeftEmpty.waitingTickets === 1 ? 'ticket stays' : 'tickets stay'} unassigned
                  </>
                )}
                .
              </span>
            </li>
          )}

          {preview.allowed &&
            !preview.pipelineChange &&
            preview.routingReferences.length === 0 &&
            !preview.parentAgentsToMove &&
            !preview.oldParentLeftEmpty && (
              <li className="flex items-start gap-2 text-gray-500 dark:text-gray-400">
                <Inbox className="mt-0.5 h-4 w-4 flex-shrink-0" />
                Nothing else changes.
              </li>
            )}
        </ul>
      )}

      <div className="flex justify-end gap-3">
        <Button variant="secondary" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(!!preview?.parentAgentsToMove)}
          disabled={isSaving || !preview?.allowed}
        >
          {isSaving ? <Spinner size="sm" /> : target ? 'Move team' : 'Move to top level'}
        </Button>
      </div>
    </div>
  );
}
